import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import Logo from 'src/components/elements/logo/Logo'
import { useLanguage } from '@hooks/useLanguage'
import { CreateRequestModal } from 'src/components/elements/modals'
import { languageOptions } from 'src/components/elements/language-segments/LanguageSegments'
import { HOW_IT_WORKS_ID } from 'src/components/pages/home/sections/how/HowItWorks'

interface IFooter {
  // a guest gets only the pages open to guests
  authed: boolean
}

const HEAD = 'mb-2 text-sm font-black tracking-[0.04em] uppercase text-ink-2'
const LINK =
  'inline-flex items-center min-h-9 text-left text-[0.9375rem] font-bold text-ink transition-colors duration-200 hover:text-brand-ink'

// The year now in Kyiv, for the copyright line
const kyivYear = (now: Date) =>
  new Intl.DateTimeFormat('en', { timeZone: 'Europe/Kyiv', year: 'numeric' }).format(now)

// The end of every scrolling page (not the map): the pages of the service,
// help and the legal texts that exist — no links to terms or an offer that
// are not written yet — the language, and what the platform vouches for.
const Footer: FC<IFooter> = ({ authed }) => {
  const { t } = useTranslation()
  const { language, setLanguage } = useLanguage()
  const [requestOpen, setRequestOpen] = useState(false)

  return (
    <footer className="bg-surface border-t border-solid border-ink/10">
      <div className="flex flex-col gap-10 w-full max-w-[96rem] mx-auto px-4 md:px-8 lg:px-16 pt-10 md:pt-12">
        <div className="grid gap-8 md:gap-10 lg:gap-16 md:grid-cols-[minmax(0,22.5rem)_repeat(3,minmax(0,1fr))]">
          <div className="flex flex-col gap-4">
            <Logo
              className="self-start h-[2.875rem]"
              imageClassName="w-[8.5rem] h-full"
              textClassName="text-base"
            />
            <p className="m-0 text-[0.9375rem] leading-[1.375rem] text-muted">
              {t('home_hero.title')}
            </p>
            <label className="relative self-start">
              <span className="sr-only">{t('language')}</span>
              <select
                value={language}
                onChange={(event) => setLanguage(event.target.value, { account: authed })}
                className="appearance-none h-11 pl-3.5 pr-9 rounded-xl border border-solid border-ink/[0.18] bg-surface text-base md:text-[0.9375rem] font-bold text-ink cursor-pointer focus:outline-none focus-visible:border-brand focus-visible:shadow-[0_0_0_0.1875rem_rgba(83,112,246,0.2)]"
              >
                {[...languageOptions].reverse().map((option) => (
                  <option key={option.value} value={option.value} lang={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none stroke-ink-2"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </label>
          </div>
          <nav aria-label={t('footer.service')} className="flex flex-col items-start gap-1">
            <h2 className={HEAD}>{t('footer.service')}</h2>
            <Link to="/" className={LINK}>
              {t('footer.map')}
            </Link>
            <Link to="/create-ad" className={LINK}>
              {t('create_ad')}
            </Link>
            {authed && (
              <button type="button" onClick={() => setRequestOpen(true)} className={LINK}>
                {t('create_a_request')}
              </button>
            )}
            {authed && (
              <Link to="/agreement" className={LINK}>
                {t('footer.agreement')}
              </Link>
            )}
          </nav>
          {authed && (
            <nav aria-label={t('footer.help')} className="flex flex-col items-start gap-1">
              <h2 className={HEAD}>{t('footer.help')}</h2>
              <Link to={`/cabinet#${HOW_IT_WORKS_ID}`} className={LINK}>
                {t('home_how.title')}
              </Link>
              <Link to="/support" className={LINK}>
                {t('support')}
              </Link>
            </nav>
          )}
          <nav aria-label={t('footer.legal')} className="flex flex-col items-start gap-1">
            <h2 className={HEAD}>{t('footer.legal')}</h2>
            <Link to="/privacy-policy" className={LINK}>
              {t('privacy.title')}
            </Link>
          </nav>
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 min-h-16 py-4 border-t border-solid border-ink/[0.08] text-sm text-ink-2">
          <span className="flex-1 min-w-[12rem]">
            {t('footer.copyright', { year: kyivYear(new Date()) })}
          </span>
          {[
            t('home_how.badge_moderation'),
            t('home_how.badge_diia'),
            t('home_how.badge_agreement'),
          ].map((badge) => (
            <span key={badge} className="inline-flex items-center gap-1.5 font-bold">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                className="stroke-success"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M5 12l5 5 9-10" />
              </svg>
              {badge}
            </span>
          ))}
        </div>
      </div>
      {authed && (
        <CreateRequestModal open={requestOpen} handleClose={() => setRequestOpen(false)} />
      )}
    </footer>
  )
}

export default Footer
